import { WORKFLOW_GLYPH } from './workflowModel'
import './workflow.css'
import { SkillGlyph } from '@shared/components/SkillGlyph'

/**
 * Build workflow (Huanrui, 2026-10-16): what the board worked out, to be
 * changed before it is merged into one card (workflowModel.ts). The chain
 * stays lit on the board meanwhile; the rest is dimmed.
 */
export interface DraftInput {
  id: string
  name: string
  mode: 'replace' | 'fixed'
  thumb: string
}
export interface DraftStep {
  id: string
  name: string
  glyph: string
  kept: boolean
  /** Its pictures come out of the workflow. */
  output: boolean
  /** Steps it feeds: it cannot be left out while one of them stays. */
  feeds: string[]
}
export interface Draft {
  name: string
  inputs: DraftInput[]
  steps: DraftStep[]
  refused: string[]
  estimate: number | null
}

export function WorkflowBuilder({
  draft,
  onChange,
  onCancel,
  onMerge,
}: {
  draft: Draft
  onChange: (draft: Draft) => void
  onCancel: () => void
  onMerge: (draft: Draft) => void
}) {
  const set = (patch: Partial<Draft>) => onChange({ ...draft, ...patch })
  const setInput = (i: number, patch: Partial<DraftInput>) =>
    set({ inputs: draft.inputs.map((one, n) => (n === i ? { ...one, ...patch } : one)) })
  const move = (i: number, by: number) => {
    const next = [...draft.inputs]
    const [one] = next.splice(i, 1)
    next.splice(Math.max(0, Math.min(next.length, i + by)), 0, one!)
    set({ inputs: next })
  }
  const setStep = (id: string, patch: Partial<DraftStep>) =>
    set({ steps: draft.steps.map((one) => (one.id === id ? { ...one, ...patch } : one)) })
  const kept = draft.steps.filter((one) => one.kept)
  const outputs = kept.filter((one) => one.output)
  const problem =
    kept.length === 0
      ? 'Keep at least one step'
      : outputs.length === 0
        ? 'Choose at least one step whose pictures come out'
        : !draft.name.trim()
          ? 'Give it a name'
          : ''

  return (
    <aside className="wfb nodrag nopan nowheel" aria-label="Build workflow">
      <div className="wfb__head">
        <span aria-hidden="true">{WORKFLOW_GLYPH}</span> Build workflow
      </div>
      <div className="wfb__body">
        <div>
          <div className="wfb__label">Name</div>
          <input className="wfb__name" value={draft.name} maxLength={60} onChange={(event) => set({ name: event.target.value })} />
        </div>

        <div>
          <div className="wfb__label">Inputs, in order</div>
          {draft.inputs.length === 0 ? <p className="wfb__note">None: every step makes its own picture.</p> : null}
          {draft.inputs.map((one, i) => (
            <div key={one.id} className="wfb__row">
              {one.thumb ? <img className="wfb__thumb" src={one.thumb} alt="" /> : <span className="wfb__glyph">▢</span>}
              <div className="wfb__field">
                <input value={one.name} maxLength={40} onChange={(event) => setInput(i, { name: event.target.value })} aria-label="Input name" />
                <span className="wfb__seg">
                  <button type="button" className={one.mode === 'replace' ? 'on' : ''} onClick={() => setInput(i, { mode: 'replace' })}>
                    New each run
                  </button>
                  <button type="button" className={one.mode === 'fixed' ? 'on' : ''} onClick={() => setInput(i, { mode: 'fixed' })}>
                    Fixed
                  </button>
                </span>
              </div>
              <span className="wfb__tools">
                <button type="button" disabled={i === 0} onClick={() => move(i, -1)} aria-label="Up">
                  ↑
                </button>
                <button type="button" disabled={i === draft.inputs.length - 1} onClick={() => move(i, 1)} aria-label="Down">
                  ↓
                </button>
              </span>
            </div>
          ))}
          <p className="wfb__note" style={{ marginTop: 6 }}>
            New each run: a port on the card. Fixed: this picture is kept in the workflow (a logo, a style).
          </p>
        </div>

        <div>
          <div className="wfb__label">Steps</div>
          {draft.steps.map((one) => {
            const needed = one.feeds.some((id) => draft.steps.find((step) => step.id === id)?.kept)
            return (
              <div key={one.id} className={`wfb__row${one.kept ? '' : ' wfb__row--off'}`}>
                <span className="wfb__glyph"><SkillGlyph glyph={one.glyph} /></span>
                <div className="wfb__field">
                  <span>{one.name}</span>
                  <label className="wfb__check">
                    <input
                      type="checkbox"
                      checked={one.output}
                      disabled={!one.kept}
                      onChange={(event) => setStep(one.id, { output: event.target.checked })}
                    />
                    Its pictures come out
                  </label>
                </div>
                <label className="wfb__check" title={needed && one.kept ? 'A step after it needs its picture' : ''}>
                  <input
                    type="checkbox"
                    checked={one.kept}
                    disabled={needed && one.kept}
                    onChange={(event) => setStep(one.id, { kept: event.target.checked })}
                  />
                  Keep
                </label>
              </div>
            )
          })}
          <p className="wfb__note" style={{ marginTop: 6 }}>
            A picture marked in the workflow (right-click, Mark) comes out too, even from the middle of it.
          </p>
        </div>

        {draft.refused.length > 0 ? (
          <p className="wfb__warn">Left out, it cannot be in a workflow yet: {draft.refused.join('; ')}.</p>
        ) : null}
        <p className="wfb__note">
          {draft.estimate === null
            ? 'Credits a run: they vary.'
            : draft.estimate === 0
              ? 'Free to run.'
              : `About ${draft.estimate} credits a run (what it took this time).`}{' '}
          An Edit area box falls in the same place on each picture.
        </p>
      </div>
      <div className="wfb__foot">
        <button type="button" onClick={onCancel}>
          Cancel
        </button>
        <button type="button" className="wfb__go" disabled={Boolean(problem)} title={problem} onClick={() => onMerge(draft)}>
          Merge into card
        </button>
      </div>
    </aside>
  )
}
